import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  ApprovalInboxEntry,
  BulkApproveFailureReason,
  BulkApproveResponse,
  OutstandingApprovalItem,
  TimesheetPeriod,
} from '@time-tracking/shared';

import {
  useApprovalInbox,
  useApprove,
  useBulkApprove,
  useApprovalItem,
  useOutstandingApprovals,
  useReportingPeriods,
  useReturn,
  useUnapprove,
} from '../api/approvals.js';
import { useMe } from '../api/hooks.js';
import { useAdminTeams, useAssignToTeam } from '../api/admin.js';
import { TeamPicker } from '../components/approvals/TeamPicker.js';
import { Layout } from '../components/Layout.js';
import { AoboConfirmDialog } from '../components/approvals/AoboConfirmDialog.js';
import {
  AssignConfirmDialog,
  type PendingAssignment,
} from '../components/approvals/AssignConfirmDialog.js';
import { BulkApproveConfirmDialog } from '../components/approvals/BulkApproveConfirmDialog.js';
import { DetailDrawer } from '../components/approvals/DetailDrawer.js';
import type { FilterTab } from '../components/approvals/FilterTabs.js';
import { FilterTabs } from '../components/approvals/FilterTabs.js';
import { InboxTable } from '../components/approvals/InboxTable.js';
import { PeriodNav } from '../components/approvals/PeriodNav.js';
import { ReturnDialog } from '../components/approvals/ReturnDialog.js';
import { SummaryCards } from '../components/approvals/SummaryCards.js';
import { TeamGroupHeader } from '../components/approvals/TeamGroupHeader.js';
import type { HistoryInitialUser } from '../components/approvals/UserHistoryView.js';
import { UserHistoryView } from '../components/approvals/UserHistoryView.js';
import { LockIcon } from '../components/icons/LockIcon.js';
import { WarningIcon } from '../components/icons/WarningIcon.js';
import { isReportingPeriodClosedError } from '../api/client.js';
import { ToastRegion } from '../components/ToastRegion.js';
import { useToasts } from '../components/useToasts.js';
import { useBatchRun, useKeyedRun } from '../hooks/useBatchRun.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { buildDrawerStepOrder, stepToUserId } from '../utils/drawerStepOrder.js';
import { chicagoTodayIso, daysBetweenIso, formatWeekRange } from '../utils/weekDates.js';
import { NeedsActionView } from '../components/approvals/NeedsActionView.js';
import { isWeekBulkApprovable } from '../components/approvals/needsActionGrouping.js';
import {
  chunkForApproval,
  describeBulkOutcome,
  failureReasonsByRowKey,
  mergeBulkResults,
} from '../components/approvals/bulkApprove.js';

/** Named rather than inlined, per the no-magic-strings rule. */
// 'week' rather than 'inbox' so the URL reads ?view=week, matching the tab's
// visible label and the selectors the e2e spec and fidelity screens use.
type ApprovalsView = 'action' | 'week' | 'history';
const APPROVALS_VIEWS: readonly ApprovalsView[] = ['action', 'week', 'history'];
function isApprovalsView(value: string | null): value is ApprovalsView {
  return value !== null && (APPROVALS_VIEWS as readonly string[]).includes(value);
}

function formatPeriodLabel(period: TimesheetPeriod): string {
  // Was a local-midnight parse (`new Date(y, m - 1, d)`), which is the exact DST
  // bug docs/solutions/runtime-errors/local-midnight-date-breaks-week-math-in-dst-timezones-2026-05-07.md
  // documents: local midnight can resolve to the previous UTC day. formatWeekRange
  // anchors at UTC noon like the rest of the week math.
  return formatWeekRange(period.weekStartDate, period.weekEndDate);
}

/** The week-view banner explaining a closed period; every withheld action points at it. */
const WEEK_LOCKED_NOTE_ID = 'approvals-week-locked-note';

/**
 * The failure toast for a single approve / return / unapprove. A closed period gets
 * its own wording because its remedy is different from a retry: nothing the approver
 * does will make the action land until an administrator reopens the period. The
 * inbox refetch the mutation triggers on settle then picks up `isLocked` and the
 * week view withholds the actions.
 */
function actionFailedMessage(verb: string, name: string, err: unknown): string {
  return isReportingPeriodClosedError(err)
    ? `Couldn't ${verb} ${name}'s timesheet — its reporting period is closed. An administrator can reopen it.`
    : `Failed to ${verb} ${name}'s timesheet.`;
}

function todayIso(): string {
  // Chicago's calendar date, not the server's. `new Date().toISOString()` is UTC,
  // so between 19:00 and midnight Central it names tomorrow and would land an
  // approver on the wrong week.
  return chicagoTodayIso();
}

function addDays(date: string, n: number): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// ---- Team grouping helpers ----

const UNASSIGNED_TEAM_ID = '__unassigned__';

interface TeamGroup {
  teamId: string;
  teamName: string;
  leadName: string | null;
  leadUserId: string | null;
  entries: ApprovalInboxEntry[];
  stuckEntries: ApprovalInboxEntry[];
  submittedEntries: ApprovalInboxEntry[];
  score: number;
  isUnassigned: boolean;
}

function teamProblemScore(entries: ApprovalInboxEntry[], stuckCount: number): number {
  const missingCount = entries.filter((e) => e.status === 'missing').length;
  const draftCount = entries.filter((e) => e.status === 'draft').length;
  const returnedCount = entries.filter((e) => e.status === 'returned').length;
  const submittedNotStuck = entries.filter((e) => e.status === 'submitted').length - stuckCount;
  return missingCount * 4 + returnedCount * 3 + draftCount * 2 + stuckCount * 2 + submittedNotStuck;
}

/**
 * Group the inbox by team, flagging the sheets that have been waiting too long.
 *
 * `today` is a parameter and must be the **Chicago** calendar date — the whole point of
 * the two-day threshold below is how long a human has been waiting, and the UTC day is
 * already tomorrow for the last five hours of every working evening.
 *
 * Exported for testing, like `outstandingApprovalsKey`: the threshold decides which
 * sheets the "approve on behalf of the lead" dialog offers to approve, which is the most
 * consequential derived value on this page and had no coverage at all.
 */
export function buildTeamGroups(entries: ApprovalInboxEntry[], today: string): TeamGroup[] {
  const byTeam = new Map<
    string,
    {
      teamName: string;
      leadName: string | null;
      leadUserId: string | null;
      entries: ApprovalInboxEntry[];
    }
  >();
  for (const e of entries) {
    const key = e.teamId ?? UNASSIGNED_TEAM_ID;
    if (!byTeam.has(key)) {
      byTeam.set(key, {
        teamName: e.teamId ? (e.teamName ?? '') : 'Unassigned',
        leadName: e.teamLeadDisplayName,
        leadUserId: e.teamLeadUserId,
        entries: [],
      });
    }
    const teamEntry = byTeam.get(key);
    if (teamEntry) teamEntry.entries.push(e);
  }

  const groups = [...byTeam.entries()].map(([teamId, group]) => {
    const isUnassigned = teamId === UNASSIGNED_TEAM_ID;
    const stuckEntries = isUnassigned
      ? []
      : group.entries.filter(
          (e) =>
            e.status === 'submitted' &&
            e.submittedAt !== null &&
            daysBetweenIso(e.submittedAt, today) >= 2,
        );
    const submittedEntries = group.entries.filter(
      (e) => e.status === 'submitted' && e.timesheetId !== null,
    );
    const score = teamProblemScore(group.entries, stuckEntries.length);
    return { teamId, ...group, stuckEntries, submittedEntries, score, isUnassigned };
  });

  return groups.sort((a, b) => {
    if (a.isUnassigned) return -1;
    if (b.isUnassigned) return 1;
    return b.score - a.score;
  });
}

// ---- Approvals page ----

export function Approvals() {
  const meQuery = useMe();

  const me = meQuery.data;
  const isAdmin = me?.role === 'administrator';
  // Landing on Needs action is the point of the rework: managers do not work one
  // week at a time, and the week stepper hid the backlog behind clicks.
  //
  // Kept in the URL so a manager can bookmark or share a view. There is no
  // ?tab= precedent in this app; Timesheet.tsx's ?week= handling is the model,
  // including plain setSearchParams for a user action and { replace: true } for
  // a correction, so normalising an unknown value leaves no history entry.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const view: ApprovalsView = isApprovalsView(rawView) ? rawView : 'action';
  const setView = useCallback(
    (next: ApprovalsView) => {
      setSearchParams(next === 'action' ? {} : { view: next });
    },
    [setSearchParams],
  );
  useEffect(() => {
    if (rawView !== null && !isApprovalsView(rawView)) {
      setSearchParams({}, { replace: true });
    }
  }, [rawView, setSearchParams]);

  // The triage row whose drawer is open, and the row being returned. Both are
  // (person, week) pairs rather than user ids: the same person appears once per
  // outstanding week, so a user id alone cannot say which sheet is meant.
  const [actionItem, setActionItem] = useState<OutstandingApprovalItem | null>(null);
  const [actionReturnItem, setActionReturnItem] = useState<OutstandingApprovalItem | null>(null);
  const [scope, setScope] = useState<'my_teams' | 'all_teams'>('my_teams');

  // Initialize scope to all_teams for admins once me loads
  useEffect(() => {
    if (isAdmin) setScope('all_teams');
  }, [isAdmin]);

  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());
  const [aoboTeam, setAoboTeam] = useState<TeamGroup | null>(null);
  const [unassignedSelectedUserIds, setUnassignedSelectedUserIds] = useState<Set<string>>(
    new Set(),
  );
  const [pendingAssign, setPendingAssign] = useState<PendingAssignment | null>(null);
  /**
   * Where focus goes when a drawer closes and the row that opened it is gone.
   *
   * The view tab is the right anchor: it is always rendered, it is what the drawer
   * belongs to, and landing there puts a keyboard user one Tab from the list rather than
   * on `<body>` at the top of the document.
   */
  const actionTabRef = useRef<HTMLButtonElement>(null);
  const [pickerState, setPickerState] = useState<{ anchorRect: DOMRect; userIds: string[] } | null>(
    null,
  );

  // Period navigation: start by resolving today's reporting period
  const [aroundDate, setAroundDate] = useState<string>(todayIso);
  const reportingPeriodsQuery = useReportingPeriods(aroundDate);

  // Once we have periods, pick the one that contains aroundDate
  const [currentWeekStartDate, setCurrentWeekStartDate] = useState<string | undefined>(undefined);
  const [currentPeriod, setCurrentPeriod] = useState<TimesheetPeriod | undefined>(undefined);

  useEffect(() => {
    const periods = reportingPeriodsQuery.data?.periods;
    if (!periods || periods.length === 0) return;

    // Find the period whose weekStartDate <= aroundDate <= weekEndDate
    const target = periods.find(
      (p) => p.weekStartDate <= aroundDate && aroundDate <= p.weekEndDate,
    );
    const chosen = target ?? periods[Math.floor(periods.length / 2)];
    if (chosen && chosen.weekStartDate !== currentWeekStartDate) {
      setCurrentWeekStartDate(chosen.weekStartDate);
      setCurrentPeriod(chosen);
    }
  }, [reportingPeriodsQuery.data, aroundDate]); // currentWeekStartDate intentionally omitted — it's what we're setting

  const inboxQuery = useApprovalInbox(currentWeekStartDate, scope);

  const approveM = useApprove();
  const returnM = useReturn();
  const unapproveM = useUnapprove();
  const bulkApproveM = useBulkApprove();
  const assignToTeamM = useAssignToTeam();
  const teamsQuery = useAdminTeams({ enabled: isAdmin });

  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // The drawer is keyed by user id rather than holding a captured entry object:
  // J/K swaps which entry is shown, and deriving from the live query means the
  // open drawer also refreshes itself after an approve/return instead of showing
  // a stale snapshot.
  const [drawerUserId, setDrawerUserId] = useState<string | null>(null);
  const [returningEntry, setReturningEntry] = useState<ApprovalInboxEntry | null>(null);
  // Person the History view opens scoped to, set by the drawer's "View full history".
  const [historyUser, setHistoryUser] = useState<HistoryInitialUser | undefined>(undefined);
  const [unapproveCommentEntry, setUnapproveCommentEntry] = useState<ApprovalInboxEntry | null>(
    null,
  );

  const { toasts, pushToast, dismiss: dismissToast } = useToasts();

  usePageTitle('Approvals');

  useEffect(() => {
    setSelectedIds(new Set());
    setUnassignedSelectedUserIds(new Set());
    setPickerState(null);
  }, [scope, currentWeekStartDate, activeFilter]);

  // Period navigation
  const handlePrev = useCallback(() => {
    if (!currentPeriod) return;
    setAroundDate(addDays(currentPeriod.weekStartDate, -7));
    setCurrentWeekStartDate(undefined);
    setCurrentPeriod(undefined);
  }, [currentPeriod]);

  const handleNext = useCallback(() => {
    if (!currentPeriod) return;
    setAroundDate(addDays(currentPeriod.weekStartDate, 7));
    setCurrentWeekStartDate(undefined);
    setCurrentPeriod(undefined);
  }, [currentPeriod]);

  const entries = inboxQuery.data?.entries ?? [];
  // A closed period owns this week: approve, return and unapprove would all be
  // refused, so the week view withholds them and says why. Rows stay listed.
  const weekLocked = inboxQuery.data?.isLocked ?? false;
  // Resolved against the *unfiltered* list: narrowing the search box while the
  // drawer is open must not yank it shut. It closes only when the person really
  // leaves the data (period change, scope switch).
  const drawerEntry = entries.find((e) => e.userId === drawerUserId) ?? null;
  const approverTeamIdSet = new Set(inboxQuery.data?.approverTeamIds ?? []);
  const isApproverForTeam = (teamId: string) =>
    scope === 'my_teams' || approverTeamIdSet.has(teamId);

  const filterCounts: Record<FilterTab, number> = {
    all: entries.length,
    pending: entries.filter((e) => e.status === 'submitted' || e.status === 'returned').length,
    warnings: entries.filter((e) => e.anomalies.some((a) => a.kind !== 'missing')).length,
    approved: entries.filter((e) => e.status === 'approved').length,
    missing: entries.filter((e) => e.status === 'missing' || e.status === 'draft').length,
  };

  const myUserId = me?.userId ?? null;
  // Both counts drive bulk affordances, so both apply R22 — otherwise a button offers a
  // number the action will not honour, or stays live with nothing it may take.
  const cleanSelectedCount = [...selectedIds].filter((id) => {
    const entry = entries.find((e) => e.timesheetId === id);
    return (
      entry !== undefined && isWeekBulkApprovable(entry, myUserId) && entry.anomalies.length === 0
    );
  }).length;
  const bulkApprovableCount = weekLocked
    ? 0
    : entries.filter((e) => isWeekBulkApprovable(e, myUserId)).length;

  // `todayIso()`, not `new Date().toISOString()`. The helper at the top of this file
  // exists for exactly this and says so; this call site was computing the UTC day
  // instead, which between 19:00 and midnight Central names tomorrow — so the
  // "stuck for 2 days" threshold below fired a day early every evening.
  const teamGroups = buildTeamGroups(entries, todayIso());

  const STATUS_SORT: Record<string, number> = {
    submitted: 0,
    returned: 1,
    draft: 2,
    missing: 3,
    approved: 4,
  };

  // Filtered + sorted entries (status priority first, then alphabetical)
  const filteredEntries = entries
    .filter((e) => {
      if (activeFilter === 'all') return true;
      if (activeFilter === 'pending') return e.status === 'submitted' || e.status === 'returned';
      if (activeFilter === 'warnings') return e.anomalies.some((a) => a.kind !== 'missing');
      if (activeFilter === 'approved') return e.status === 'approved';
      if (activeFilter === 'missing') return e.status === 'missing' || e.status === 'draft';
      return true;
    })
    .filter((e) => {
      if (!search.trim()) return true;
      return e.name.toLowerCase().includes(search.toLowerCase().trim());
    })
    .sort((a, b) => {
      const diff = (STATUS_SORT[a.status] ?? 99) - (STATUS_SORT[b.status] ?? 99);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name);
    });

  // J/K step order — mirrors what the approver sees, not the flat sorted array.
  // Held in a ref so `handleDrawerStep` can have empty deps: the drawer's keydown
  // effect depends on that callback, and a callback that changed identity every
  // render would tear down and re-bind the document listener every render.
  // `teamGroups` and `filteredEntries` are rebuilt each render, so memoising on
  // them would not have helped.
  const stepOrderRef = useRef<string[]>([]);
  stepOrderRef.current = buildDrawerStepOrder(teamGroups, filteredEntries, collapsedTeams);

  const handleDrawerStep = useCallback((delta: -1 | 1) => {
    setDrawerUserId((current) => stepToUserId(stepOrderRef.current, current, delta) ?? current);
  }, []);

  // Summary counts
  const submittedCount = entries.filter((e) => e.status === 'submitted').length;
  const pendingCount = entries.filter(
    (e) => e.status === 'submitted' || e.status === 'returned',
  ).length;
  const warningsCount = entries.filter((e) => e.anomalies.some((a) => a.kind !== 'missing')).length;
  const approvedCount = entries.filter((e) => e.status === 'approved').length;
  const missingCount = entries.filter((e) => e.status === 'missing' || e.status === 'draft').length;

  // Pending action IDs — tracked in state so multiple in-flight actions are all reflected.
  // Per-sheet, synchronously guarded. The drawer and the week table both approve one
  // sheet at a time, and a plain state Set cannot stop a same-tick double activation:
  // two clicks before React re-renders the button as disabled both read the old value
  // and send two requests under different idempotency keys, so the first approves and
  // the second is refused — a failure toast for a sheet that was just approved.
  const approveRun = useKeyedRun();
  const [pendingUnapproveIds, setPendingUnapproveIds] = useState<Set<string>>(new Set());

  const handleApprove = useCallback(
    (entry: ApprovalInboxEntry) => {
      if (!entry.timesheetId) return;
      const id = entry.timesheetId;
      void approveRun.run(id, async () => {
        try {
          await approveM.mutateAsync({
            timesheetId: id,
            weekStartDate: currentWeekStartDate,
            scope,
          });
          pushToast(`Approved ${entry.name}'s timesheet.`);
          // Functional form: J/K may have stepped the drawer to someone else
          // while this request was in flight, and a captured id would close
          // whoever is showing now instead of the person just actioned.
          setDrawerUserId((current) => (current === entry.userId ? null : current));
        } catch (err) {
          pushToast(actionFailedMessage('approve', entry.name, err), 'error');
        }
      });
    },
    [approveM, approveRun, currentWeekStartDate, scope, pushToast],
  );

  const handleOpenReturn = useCallback((entry: ApprovalInboxEntry) => {
    setReturningEntry(entry);
  }, []);

  const handleSubmitReturn = useCallback(
    (entry: ApprovalInboxEntry, comment: string) => {
      if (!entry.timesheetId) return;
      returnM.mutate(
        { timesheetId: entry.timesheetId, comment, weekStartDate: currentWeekStartDate, scope },
        {
          onSuccess: () => {
            setReturningEntry(null);
            pushToast(`Returned ${entry.name}'s timesheet for correction.`, 'warn');
            // Functional form: J/K may have stepped the drawer to someone else
            // while this request was in flight, and a captured id would close
            // whoever is showing now instead of the person just actioned.
            setDrawerUserId((current) => (current === entry.userId ? null : current));
          },
          onError: (err) => pushToast(actionFailedMessage('return', entry.name, err), 'error'),
        },
      );
    },
    [returnM, currentWeekStartDate, scope, pushToast],
  );

  const handleUnapprove = useCallback((entry: ApprovalInboxEntry) => {
    // For unapprove we also need a comment — reuse the ReturnDialog with a different entry slot
    setUnapproveCommentEntry(entry);
  }, []);

  const handleSubmitUnapprove = useCallback(
    (entry: ApprovalInboxEntry, comment: string) => {
      if (!entry.timesheetId) return;
      const id = entry.timesheetId;
      setUnapproveCommentEntry(null);
      setPendingUnapproveIds((prev) => new Set([...prev, id]));
      unapproveM.mutate(
        { timesheetId: id, comment, weekStartDate: currentWeekStartDate, scope },
        {
          onSuccess: () => {
            setPendingUnapproveIds((prev) => {
              const s = new Set(prev);
              s.delete(id);
              return s;
            });
            pushToast(`Unapproved ${entry.name}'s timesheet.`, 'warn');
            // Functional form: J/K may have stepped the drawer to someone else
            // while this request was in flight, and a captured id would close
            // whoever is showing now instead of the person just actioned.
            setDrawerUserId((current) => (current === entry.userId ? null : current));
          },
          onError: (err) => {
            setPendingUnapproveIds((prev) => {
              const s = new Set(prev);
              s.delete(id);
              return s;
            });
            pushToast(actionFailedMessage('unapprove', entry.name, err), 'error');
          },
        },
      );
    },
    [unapproveM, currentWeekStartDate, scope, pushToast],
  );

  /**
   * Send a week-view bulk approve in cap-sized chunks and fold the results.
   *
   * `BulkApproveRequest` caps `timesheetIds` at BULK_APPROVE_MAX_ITEMS. These three
   * call sites predate that cap and used to post the whole array, so an admin on
   * "All teams" with more than 200 submitted sheets in one week got a single 400 for a
   * request that had always worked. The cross-week view already chunks; this gives the
   * week view the same treatment rather than leaving a shared endpoint with two
   * different client contracts.
   */
  // Two batches on this page fan out or iterate across several requests, and a mutation's
  // own `isPending` follows only its latest call — so it reports false mid-batch and lets
  // a second batch start underneath the first. Both use the same mechanism rather than a
  // boolean each; see `useBatchRun`.
  const bulkRun = useBatchRun();
  const assignRun = useBatchRun();

  /**
   * Send a selection as one guarded batch, chunked to fit the request cap.
   *
   * The batch guard lives HERE, at the one point both bulk paths funnel through, rather
   * than in a caller. An earlier revision wrapped only `runApproveItems`, which left the
   * By-week and AOBO paths with no whole-batch pending state at all — their buttons
   * stayed live and a second activation could start an overlapping chunk sequence under
   * a fresh idempotency key. A guard a caller has to remember to apply is one a caller
   * will eventually forget.
   *
   * Resolves to `undefined` when it declines because a batch is already running.
   */
  const sendChunkedBulkApprove = useCallback(
    async (
      ids: string[],
    ): Promise<{ merged: BulkApproveResponse; failedEarly: boolean } | undefined> =>
      bulkRun.run(async () => {
        const settled: BulkApproveResponse[] = [];
        try {
          for (const chunk of chunkForApproval(ids)) {
            // Sequential rather than parallel: the server processes each item in its own
            // transaction, and firing every chunk at once would just move the timeout from
            // one request to several.
            settled.push(
              await bulkApproveM.mutateAsync({
                timesheetIds: chunk,
                weekStartDate: currentWeekStartDate,
                scope,
              }),
            );
          }
        } catch {
          // A chunk threw. Earlier chunks are already approved AND PUBLISHED to the export,
          // with no undo, so the caller must be able to say what landed rather than
          // claiming nothing did. The week-view paths used to report total failure here.
          return { merged: mergeBulkResults(settled), failedEarly: true };
        }
        return { merged: mergeBulkResults(settled), failedEarly: false };
      }),
    [bulkApproveM, bulkRun, currentWeekStartDate, scope],
  );

  /**
   * Report a week-view bulk approve.
   *
   * Routed through `describeBulkOutcome` like the triage view rather than reading
   * `approved.length` alone: the server can now refuse an item outright — a closed
   * period, or the approver's own sheet under the `self_approval` rule — and a bare
   * success count reports those as if nothing had happened, leaving the sheet
   * submitted with no indication on screen.
   */
  const runWeekBulkApprove = useCallback(
    async (ids: string[], onSettledSuccess?: () => void): Promise<void> => {
      // `undefined` means a batch was already running and this one was declined; the
      // run in flight will report itself.
      const settled = await sendChunkedBulkApprove(ids);
      if (!settled) return;
      const { merged, failedEarly } = settled;
      if (failedEarly) {
        pushToast(
          merged.approved.length > 0
            ? `Approved ${merged.approved.length} of ${ids.length} before the request failed.`
            : 'Nothing was approved.',
          'error',
        );
        return;
      }
      const outcome = describeBulkOutcome(merged, ids.length, 1);
      pushToast(outcome.message, outcome.kind);
      if (!outcome.keepSelection) onSettledSuccess?.();
    },
    [sendChunkedBulkApprove, pushToast],
  );

  const handleBulkApprove = useCallback(
    (clean: boolean) => {
      // R22: the approver's own sheet is individually approvable and excluded from every
      // bulk path. This one had no exclusion, so the server guard added earlier in this
      // review turned an ordinary "Approve all" into a partial failure whenever the
      // approver's own week was in front of them.
      const toApprove = entries.filter(
        (e) => isWeekBulkApprovable(e, myUserId) && (!clean || e.anomalies.length === 0),
      );
      if (toApprove.length === 0) {
        pushToast('No timesheets to approve.', 'warn');
        return;
      }
      const ids = [
        ...new Set(toApprove.map((e) => e.timesheetId).filter((id): id is string => id !== null)),
      ];
      void runWeekBulkApprove(ids);
    },
    [entries, myUserId, runWeekBulkApprove],
  );

  const handleBulkApproveSelected = useCallback(
    (cleanOnly: boolean) => {
      const ids = [...selectedIds].filter((id) => {
        const entry = entries.find((e) => e.timesheetId === id);
        if (!entry || !isWeekBulkApprovable(entry, myUserId)) return false;
        return !cleanOnly || entry.anomalies.length === 0;
      });
      if (ids.length === 0) return;
      void runWeekBulkApprove(ids, () => setSelectedIds(new Set()));
    },
    [selectedIds, entries, myUserId, runWeekBulkApprove],
  );

  const handleAoboConfirm = useCallback(
    (timesheetIds: string[]) => {
      if (timesheetIds.length === 0) return;
      void runWeekBulkApprove(timesheetIds, () => setAoboTeam(null));
    },
    [runWeekBulkApprove],
  );

  const handleOpenPicker = useCallback((userIds: string[], anchorRect: DOMRect) => {
    setPickerState({ userIds, anchorRect });
  }, []);

  // Assigning from inside the drawer reuses the inbox's picker flow verbatim —
  // same TeamPicker, same mutation, same toast. The drawer stays open behind it.
  const handleAssignFromDrawer = useCallback(
    (entry: ApprovalInboxEntry, anchorRect: DOMRect) => {
      handleOpenPicker([entry.userId], anchorRect);
    },
    [handleOpenPicker],
  );

  // Drawer → History handoff: close the drawer and switch the page to History
  // already scoped to that person, so the approver skips the search step.
  const handleViewHistory = useCallback((entry: ApprovalInboxEntry) => {
    setDrawerUserId(null);
    // The triage drawer is a separate piece of state and was left mounted, stranding a
    // modal over the History view with no way back to what opened it.
    setActionItem(null);
    setHistoryUser({
      userId: entry.userId,
      displayName: entry.name,
      initials: entry.initials,
      utEid: entry.utEid,
    });
    setView('history');
  }, []);

  // Badge count. Unfiltered on purpose: while the triage view is showing with its
  // own default filters the query key matches exactly, so TanStack serves one
  // request for both. Only once the manager filters does this become a second.
  const outstandingBadge = useOutstandingApprovals(scope, 'everything', '');

  // Picking a team stages a confirmation rather than committing: assignment carries no
  // effective date, so the new lead inherits every outstanding week the person has. U9
  // makes telling the manager what they are about to inherit the condition of that trade.
  const handlePickTeam = useCallback(
    (teamId: string) => {
      if (!pickerState) return;
      const { userIds } = pickerState;
      const teamName = teamsQuery.data?.find((t) => t.teamId === teamId)?.teamName ?? 'team';
      const affected = (outstandingBadge.data?.items ?? []).filter((i) =>
        userIds.includes(i.userId),
      );
      setPickerState(null);
      setPendingAssign({
        userIds,
        teamId,
        teamName,
        personName: userIds.length === 1 ? (affected[0]?.name ?? 'this person') : null,
        weekStartDates: [...new Set(affected.map((i) => i.weekStartDate))].sort(),
        // The badge query is row-capped and may still be loading, so an empty list is
        // only trustworthy when the query has settled and was not truncated.
        weeksKnown:
          !outstandingBadge.isPending &&
          !outstandingBadge.isError &&
          outstandingBadge.data?.capped !== true,
      });
    },
    [pickerState, teamsQuery.data, outstandingBadge],
  );

  const confirmAssign = useCallback(async () => {
    if (!pendingAssign) return;
    const { userIds, teamId, teamName } = pendingAssign;
    setPendingAssign(null);

    // `mutateAsync` + `allSettled`, not `mutate` with per-call callbacks in a loop.
    // A TanStack mutation has one observer, and firing it again before the previous
    // call settles replaces the callbacks attached to it — so with several people
    // selected the earlier completions could run no callback at all. `done + failed`
    // then never reached `userIds.length`, and the branch below that clears the
    // selection and reports the outcome simply never ran: the rows stayed checked and
    // no toast appeared, for assignments that had in fact succeeded. Settling the whole
    // batch and summarising once removes the counter entirely.
    //
    // Wrapped in the batch guard for the same reason the bulk approve is: `allSettled`
    // fans out across N users on ONE observer, so `assignToTeamM.isPending` reports the
    // latest call and goes false while earlier assignments are still running. Everything
    // gated on it re-enabled mid-batch — including, until now, nothing at all on the
    // Needs-action row, which never had a guard — so an administrator could start an
    // overlapping batch for the same person and get racing team changes with two
    // contradictory toasts.
    await assignRun.run(async () => {
      const results = await Promise.allSettled(
        userIds.map((userId) => assignToTeamM.mutateAsync({ userId, teamId })),
      );
      const done = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - done;

      setUnassignedSelectedUserIds(new Set());
      if (failed > 0) {
        pushToast(`Assigned ${String(done)}, ${String(failed)} failed.`, 'error');
      } else {
        const label = done === 1 ? '1 person' : `${String(done)} people`;
        pushToast(`Assigned ${label} to ${teamName}.`);
      }
    });
  }, [pendingAssign, assignToTeamM, assignRun, pushToast]);

  const isLoading = meQuery.isLoading || reportingPeriodsQuery.isLoading || inboxQuery.isLoading;
  const isError = meQuery.isError || reportingPeriodsQuery.isError || inboxQuery.isError;

  const outstandingCount = outstandingBadge.data?.awaitingActionCount ?? 0;

  const actionDetail = useApprovalItem(
    actionItem?.userId ?? null,
    actionItem?.weekStartDate ?? null,
    scope,
  );

  const isActionDrawer = actionItem !== null;
  const activeDrawerEntry = isActionDrawer ? (actionDetail.data?.entry ?? null) : drawerEntry;
  const actionPeriodLabel = actionItem
    ? `${formatWeekRange(actionItem.weekStartDate, actionItem.weekEndDate)}${
        actionItem.daysPastDue > 0 ? ` \u00b7 ${actionItem.daysPastDue}d past due` : ''
      }`
    : null;

  // Returns the promise, and uses `mutateAsync` rather than `mutate` with per-call
  // callbacks, for two reasons that turn out to be the same reason. The caller reads
  // "did this return a thenable?" as the signal that a row really was dispatched, which
  // is what lets it claim the row as the viewer's own change instead of announcing the
  // row's disappearance back to them as a stranger's edit. And a mutation has one
  // observer: approving several rows in quick succession replaces the callbacks
  // attached to it, so the earlier toasts could simply never fire.
  const handleApproveItem = useCallback(
    (item: OutstandingApprovalItem) => {
      if (!item.timesheetId) return;
      return approveM
        .mutateAsync({ timesheetId: item.timesheetId, weekStartDate: item.weekStartDate, scope })
        .then(
          () => {
            pushToast(`Approved ${item.name}'s sheet.`, 'success');
            setActionItem((current) => (current?.key === item.key ? null : current));
          },
          (err: unknown) => {
            pushToast(actionFailedMessage('approve', item.name, err), 'error');
            // Rethrow: the caller distinguishes a settled failure from a success, and
            // releases its claim on the row when the request did not land.
            throw new Error('approve failed');
          },
        );
    },
    [approveM, scope, pushToast],
  );

  // Above this, a bulk approve confirms first. It publishes hours to the
  // cost-center export immediately and this view has no undo, so a stray click
  // on a large selection is not something to find out about afterwards.
  const BULK_CONFIRM_THRESHOLD = 10;
  /**
   * The selection awaiting confirmation, plus the settlers for the promise
   * `handleApproveItems` handed back.
   *
   * A deferred rather than a bare list, so the over-threshold path still returns a
   * thenable to `dispatchAndClaim`. Returning void there meant a CONFIRMED bulk approve
   * was never claimed as the viewer's own: the rows vanished, reconciliation saw
   * unclaimed removals, and "11 selected sheets changed and were deselected" landed on
   * top of "Approved 11 sheets" — telling a manager a colleague beat them to sheets they
   * had just approved themselves. Cancelling rejects, which releases the claim, so the
   * property round 2 added (a cancelled confirmation must not leave keys armed) still
   * holds.
   */
  const [pendingBulk, setPendingBulk] = useState<{
    items: OutstandingApprovalItem[];
    settle: (run: Promise<void> | null) => void;
  } | null>(null);
  const [keepSelectedKeys, setKeepSelectedKeys] = useState<string[] | null>(null);
  /** Row key → why the last bulk approve refused it (R23). */
  const [bulkFailures, setBulkFailures] = useState<Record<string, BulkApproveFailureReason>>({});

  const runApproveItems = useCallback(
    async (items: OutstandingApprovalItem[]) => {
      const ids = items.map((i) => i.timesheetId).filter((id): id is string => id !== null);
      if (ids.length === 0) return;
      const weeks = new Set(items.map((i) => i.weekStartDate)).size;
      // `undefined` means the run was declined because one is already going. Say nothing:
      // the batch the user is watching is still running and will report itself.
      const settled = await sendChunkedBulkApprove(ids);
      if (!settled) return;
      const { merged, failedEarly } = settled;
      if (failedEarly) {
        pushToast(
          merged.approved.length > 0
            ? `Approved ${merged.approved.length} of ${ids.length} before the request failed.`
            : 'Nothing was approved.',
          'error',
        );
        setKeepSelectedKeys(items.map((i) => i.key));
        setBulkFailures(failureReasonsByRowKey(merged, items));
        return;
      }
      const outcome = describeBulkOutcome(merged, ids.length, weeks);
      pushToast(outcome.message, outcome.kind);
      setKeepSelectedKeys(outcome.keepSelection ? items.map((i) => i.key) : null);
      // R23: the named reason belongs against the row, not only in a count. The toast
      // can say "2 couldn't be approved"; only the row can say which two and why, and
      // the remedies are not interchangeable — a closed period is final, a permission
      // problem needs an administrator, a stale request just needs sending again.
      setBulkFailures(failureReasonsByRowKey(merged, items));
    },
    [sendChunkedBulkApprove, pushToast],
  );

  const handleApproveItems = useCallback(
    (items: OutstandingApprovalItem[]) => {
      if (items.length > BULK_CONFIRM_THRESHOLD) {
        // Still a promise, because these rows WILL be acted on if the manager confirms —
        // and the caller reads a thenable as "claim these rows as ours". It settles when
        // the run does, or rejects on cancel so the claim is released.
        return new Promise<void>((resolve, reject) => {
          setPendingBulk({
            items,
            settle: (run) => (run === null ? reject(new Error('cancelled')) : resolve(run)),
          });
        });
      }
      return runApproveItems(items);
    },
    [runApproveItems],
  );

  const handleSubmitActionReturn = useCallback(
    (item: OutstandingApprovalItem, comment: string) => {
      if (!item.timesheetId) return;
      returnM.mutate(
        // The item's own week, not the inbox's: this row can be months older
        // than whatever week the By-week tab happens to be parked on.
        { timesheetId: item.timesheetId, comment, weekStartDate: item.weekStartDate, scope },
        {
          onSuccess: () => {
            setActionReturnItem(null);
            pushToast(`Returned ${item.name}'s timesheet for correction.`, 'warn');
          },
          onError: (err) => pushToast(actionFailedMessage('return', item.name, err), 'error'),
        },
      );
    },
    [returnM, scope, pushToast],
  );

  return (
    <Layout>
      <div className="approvals-page">
        <div className="page-header approvals-header">
          <div className="approvals-header__title-group">
            <h1>Approvals</h1>
            {isAdmin && (
              <div className="scope-toggle" role="group" aria-label="Approval scope">
                <button
                  type="button"
                  aria-pressed={scope === 'my_teams'}
                  className={`scope-tab${scope === 'my_teams' ? ' scope-tab--active' : ''}`}
                  onClick={() => setScope('my_teams')}
                >
                  Your teams
                </button>
                <button
                  type="button"
                  aria-pressed={scope === 'all_teams'}
                  className={`scope-tab${scope === 'all_teams' ? ' scope-tab--active' : ''}`}
                  onClick={() => setScope('all_teams')}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" />
                  </svg>
                  All teams
                  <span className="admin-badge" aria-label="Administrator access">
                    admin
                  </span>
                </button>
              </div>
            )}
          </div>
          <div className="approvals-header__view-toggle" role="group" aria-label="Approvals view">
            <button
              type="button"
              aria-pressed={view === 'action'}
              ref={actionTabRef}
              className={`scope-tab${view === 'action' ? ' scope-tab--active' : ''}`}
              onClick={() => setView('action')}
            >
              Needs action
              {outstandingCount > 0 && (
                <span className="scope-tab__count" aria-label={`${outstandingCount} outstanding`}>
                  {outstandingCount}
                </span>
              )}
            </button>
            <button
              type="button"
              aria-pressed={view === 'week'}
              className={`scope-tab${view === 'week' ? ' scope-tab--active' : ''}`}
              onClick={() => {
                setView('week');
                // Release the drawer's handoff, so opening History from the toggle
                // later starts on the search prompt rather than silently reopening
                // whoever was last handed over.
                setHistoryUser(undefined);
              }}
            >
              By week
            </button>
            <button
              type="button"
              aria-pressed={view === 'history'}
              className={`scope-tab${view === 'history' ? ' scope-tab--active' : ''}`}
              onClick={() => setView('history')}
            >
              History
            </button>
          </div>
          {/* Week-view only. These act on the currently-shown week's inbox, so on
              the cross-week view they would approve a different set than the one
              on screen — and the triage view has its own hero bulk action. */}
          {view === 'week' && scope === 'my_teams' && (
            <div className="approvals-header__actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => handleBulkApprove(true)}
                disabled={bulkRun.pending || bulkApprovableCount === 0}
                aria-describedby={weekLocked ? WEEK_LOCKED_NOTE_ID : undefined}
                aria-label="Approve all clean timesheets (no anomalies)"
              >
                Approve all clean
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => handleBulkApprove(false)}
                disabled={bulkRun.pending || bulkApprovableCount === 0}
                aria-describedby={weekLocked ? WEEK_LOCKED_NOTE_ID : undefined}
                aria-label="Approve all pending timesheets"
              >
                Approve all
              </button>
            </div>
          )}
        </div>
        {view === 'week' &&
          (() => {
            const teamCount = teamGroups.filter((g) => !g.isUnassigned).length;
            const peopleCount = new Set(entries.map((e) => e.userId)).size;
            const scopeSuffix = scope === 'my_teams' ? '' : ' org-wide';
            return (
              <p className="approvals-header__meta" aria-live="polite">
                {`${submittedCount} sheet${submittedCount === 1 ? '' : 's'} submitted · ${teamCount} team${teamCount === 1 ? '' : 's'} · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}${scopeSuffix}`}
              </p>
            );
          })()}
        {view === 'action' && (
          <NeedsActionView
            scope={scope}
            isAdmin={isAdmin}
            currentUserId={me?.userId ?? null}
            onOpenItem={setActionItem}
            onApprove={handleApproveItem}
            onReturn={setActionReturnItem}
            onApproveMany={handleApproveItems}
            onAssignTeam={(item, anchorRect) =>
              setPickerState({ userIds: [item.userId], anchorRect })
            }
            onAnnounce={(message) => pushToast(message, 'warn')}
            keepSelectedKeys={keepSelectedKeys}
            failureReasons={bulkFailures}
            isBatchPending={bulkRun.pending || assignRun.pending}
            onOpenWeek={(weekStartDate) => {
              setAroundDate(weekStartDate);
              setCurrentWeekStartDate(undefined);
              setCurrentPeriod(undefined);
              setView('week');
            }}
          />
        )}

        {view === 'history' && <UserHistoryView isAdmin={isAdmin} initialUser={historyUser} />}

        {view === 'week' && (
          <>
            <PeriodNav
              label={currentPeriod ? formatPeriodLabel(currentPeriod) : '—'}
              onPrev={handlePrev}
              onNext={handleNext}
            />

            {/* Always mounted, so the live region exists before its text arrives: a
                region inserted already holding its message is often not announced,
                and stepping onto a closed week is exactly when it needs to be. */}
            <div role="status">
              {weekLocked && !isLoading && !isError && (
                <div id={WEEK_LOCKED_NOTE_ID} className="ts-period-closed-banner">
                  <LockIcon />
                  <span>
                    This reporting period is closed, so approvals for this week are locked: sheets
                    can&rsquo;t be approved, returned or unapproved. An administrator can reopen the
                    period if something needs to change.
                  </span>
                </div>
              )}
            </div>

            {isLoading && (
              <div aria-live="polite" aria-busy="true" className="approvals-loading">
                <span className="sr-only">Loading approvals…</span>
                <div className="skeleton skeleton--heading" aria-hidden="true" />
                <div className="skeleton skeleton--line" aria-hidden="true" />
                <div className="skeleton skeleton--line" aria-hidden="true" />
              </div>
            )}

            {isError && !isLoading && (
              <div className="error-banner" role="alert">
                <WarningIcon className="error-banner__icon" />
                <span>Failed to load approvals. Check your connection.</span>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => {
                    void meQuery.refetch();
                    void reportingPeriodsQuery.refetch();
                    void inboxQuery.refetch();
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !isError && !currentPeriod && (
              <div className="approvals-empty" role="status">
                No reporting period found for this date. Try navigating to a different week.
              </div>
            )}

            {!isLoading && !isError && currentPeriod && (
              <>
                <SummaryCards
                  pending={pendingCount}
                  warnings={warningsCount}
                  approved={approvedCount}
                  missing={missingCount}
                />

                <div className="inbox-toolbar">
                  <div className="inbox-search">
                    <span className="inbox-search__icon" aria-hidden="true">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </span>
                    <label htmlFor="inbox-search-input" className="sr-only">
                      Search by name
                    </label>
                    <input
                      id="inbox-search-input"
                      type="search"
                      className="inbox-search__input"
                      placeholder="Search by name…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <FilterTabs
                    active={activeFilter}
                    onChange={setActiveFilter}
                    counts={filterCounts}
                  />
                </div>

                {selectedIds.size > 0 && !weekLocked && (
                  <div className="bulk-action-bar" role="group" aria-label="Bulk actions">
                    <span className="bulk-action-bar__count">{selectedIds.size} selected</span>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => handleBulkApproveSelected(true)}
                      disabled={bulkRun.pending || cleanSelectedCount === 0}
                    >
                      Approve clean only ({cleanSelectedCount})
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => handleBulkApproveSelected(false)}
                      disabled={bulkRun.pending}
                    >
                      Approve {selectedIds.size}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setSelectedIds(new Set())}
                      aria-label="Clear selection"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <div className="team-groups">
                  {teamGroups.map((group) => {
                    const approverTeam = group.isUnassigned || isApproverForTeam(group.teamId);
                    const tableEntries = group.isUnassigned
                      ? filteredEntries.filter((e) => e.teamId === null)
                      : filteredEntries.filter((e) => e.teamId === group.teamId);
                    return (
                      <div
                        key={group.teamId}
                        className={`team-group${group.isUnassigned ? ' team-group--unassigned' : ''}`}
                      >
                        <TeamGroupHeader
                          teamId={group.teamId}
                          teamName={group.teamName}
                          leadName={group.leadName}
                          entries={group.entries}
                          stuckCount={group.stuckEntries.length}
                          isApproverTeam={approverTeam}
                          isCollapsed={collapsedTeams.has(group.teamId)}
                          isUnassigned={group.isUnassigned}
                          periodClosed={weekLocked}
                          onToggle={() =>
                            setCollapsedTeams((prev) => {
                              const next = new Set(prev);
                              if (next.has(group.teamId)) {
                                next.delete(group.teamId);
                              } else {
                                next.add(group.teamId);
                              }
                              return next;
                            })
                          }
                          onAobo={() =>
                            setAoboTeam(
                              approverTeam
                                ? group
                                : { ...group, stuckEntries: group.submittedEntries },
                            )
                          }
                        />
                        {!collapsedTeams.has(group.teamId) && (
                          <div id={`team-group-body-${group.teamId}`}>
                            {group.isUnassigned && unassignedSelectedUserIds.size > 0 && (
                              <div
                                className="bulk-assign-bar"
                                role="group"
                                aria-label="Bulk assign"
                              >
                                <span className="bulk-assign-bar__count">
                                  {unassignedSelectedUserIds.size} selected
                                </span>
                                <button
                                  type="button"
                                  className="assign-team-btn"
                                  disabled={assignRun.pending}
                                  onClick={(e) =>
                                    handleOpenPicker(
                                      [...unassignedSelectedUserIds],
                                      e.currentTarget.getBoundingClientRect(),
                                    )
                                  }
                                >
                                  Assign {unassignedSelectedUserIds.size} to team…
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost"
                                  onClick={() => setUnassignedSelectedUserIds(new Set())}
                                  aria-label="Clear selection"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                            <InboxTable
                              entries={tableEntries}
                              pendingApproveIds={approveRun.pending}
                              pendingUnapproveIds={pendingUnapproveIds}
                              onRowClick={(e) => setDrawerUserId(e.userId)}
                              onApprove={handleApprove}
                              onReturn={handleOpenReturn}
                              onUnapprove={handleUnapprove}
                              selectable={approverTeam && !group.isUnassigned && !weekLocked}
                              periodClosed={weekLocked}
                              selectedIds={selectedIds}
                              onSelectionChange={setSelectedIds}
                              reviewOnly={!approverTeam}
                              assignMode={group.isUnassigned}
                              onOpenPicker={handleOpenPicker}
                              selectedUserIds={unassignedSelectedUserIds}
                              onUserSelectionChange={setUnassignedSelectedUserIds}
                              {...(me?.userId !== undefined ? { currentUserId: me.userId } : {})}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {teamGroups.length === 0 && (
                    <div className="approvals-empty" role="status">
                      No teams found.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <DetailDrawer
        entry={activeDrawerEntry}
        onClose={() => {
          setActionItem(null);
          setDrawerUserId(null);
        }}
        onApprove={handleApprove}
        onReturn={handleOpenReturn}
        onUnapprove={handleUnapprove}
        isApprovePending={approveM.isPending}
        isUnapprovePending={unapproveM.isPending}
        // The triage list never carries a closed-period week (the server suppresses
        // them), so only the By-week drawer can be showing one.
        periodClosed={!isActionDrawer && weekLocked}
        canTakeAction={
          activeDrawerEntry
            ? isAdmin ||
              activeDrawerEntry.teamId === null ||
              isApproverForTeam(activeDrawerEntry.teamId)
            : true
        }
        // From activeDrawerEntry, not drawerEntry: the latter is set only by the
        // By-week table, so on the triage path it is always null and this was always
        // false — hiding the "Approve on behalf of" ceremony on exactly the view whose
        // rows carry the aobo flag. actionItem.aobo is the server's own verdict, so it
        // wins where the triage drawer supplied it.
        isAobo={
          isActionDrawer
            ? (actionItem?.aobo ?? false)
            : activeDrawerEntry
              ? isAdmin &&
                activeDrawerEntry.teamId !== null &&
                !isApproverForTeam(activeDrawerEntry.teamId)
              : false
        }
        periodLabel={actionPeriodLabel}
        pending={
          isActionDrawer && actionItem && !actionDetail.data
            ? {
                name: actionItem.name,
                periodLabel: formatWeekRange(actionItem.weekStartDate, actionItem.weekEndDate),
              }
            : null
        }
        hasError={isActionDrawer && actionDetail.isError}
        onRetry={() => void actionDetail.refetch()}
        onViewHistory={handleViewHistory}
        {...(isActionDrawer ? {} : { onStep: handleDrawerStep })}
        onAssign={handleAssignFromDrawer}
        isPickerOpen={pickerState !== null}
        // Every layer that can open above the drawer, so its trap and Escape handler
        // stand down for all of them rather than only for the picker. Each of these is
        // reachable from inside the drawer itself.
        restoreFocusRef={actionTabRef}
        isModalAbove={
          returningEntry !== null ||
          actionReturnItem !== null ||
          unapproveCommentEntry !== null ||
          aoboTeam !== null ||
          pendingAssign !== null ||
          pendingBulk !== null
        }
      />

      <ReturnDialog
        entry={returningEntry}
        onClose={() => setReturningEntry(null)}
        onSubmit={handleSubmitReturn}
        isPending={returnM.isPending}
      />

      <BulkApproveConfirmDialog
        items={pendingBulk?.items ?? null}
        onCancel={() => {
          pendingBulk?.settle(null);
          setPendingBulk(null);
        }}
        onConfirm={(items) => {
          // Hand the run back to the awaiting caller rather than starting it here; that
          // is what keeps the claim and the focus rescue attached to it.
          pendingBulk?.settle(runApproveItems(items));
          setPendingBulk(null);
        }}
      />

      <ReturnDialog
        entry={actionReturnItem}
        onClose={() => setActionReturnItem(null)}
        onSubmit={handleSubmitActionReturn}
        isPending={returnM.isPending}
      />

      <ReturnDialog
        entry={unapproveCommentEntry}
        onClose={() => setUnapproveCommentEntry(null)}
        onSubmit={handleSubmitUnapprove}
        isPending={unapproveM.isPending}
        variant="unapprove"
      />

      <AoboConfirmDialog
        team={aoboTeam}
        today={todayIso()}
        onClose={() => setAoboTeam(null)}
        onConfirm={handleAoboConfirm}
        isPending={bulkRun.pending}
      />

      <AssignConfirmDialog
        pending={pendingAssign}
        onCancel={() => setPendingAssign(null)}
        onConfirm={confirmAssign}
        isPending={assignRun.pending}
      />
      {pickerState && (
        <TeamPicker
          anchorRect={pickerState.anchorRect}
          count={pickerState.userIds.length}
          teams={teamsQuery.data ?? []}
          onPick={handlePickTeam}
          onClose={() => setPickerState(null)}
        />
      )}

      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </Layout>
  );
}
